import React, { createContext, useContext, useReducer, useMemo, useCallback, useEffect, useState } from 'react'

import { client } from '../apollo/client'
import {
  PAIR_DATA,
  PAIR_CHART,
  FILTERED_TRANSACTIONS,
  PAIRS_CURRENT,
  PAIRS_BULK,
  PAIRS_HISTORICAL_BULK,
  HOURLY_PAIR_RATES,
  DAILY_PAIR_STATS,
} from '../apollo/queries'
import { fetchHourlyPairVolumes } from '../utils/hourlyStats'
import useTransactionFeed from '../hooks/useTransactionFeed'

import { useNrgPrice } from './GlobalData'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

import {
  getPercentChange,
  get2DayPercentChange,
  isAddress,
  getBlocksFromTimestamps,
  getTimestampsForChanges,
  splitQuery,
} from '../utils'

import getEnvironmentVar from '../utils/getEnvironmentVar'
import { timeframeOptions } from '../constants'
import { useLatestBlocks } from './Application'

const WNRG_ADDRESS = getEnvironmentVar('WNRG_ADDRESS')
const CHARTS_START_TIME = getEnvironmentVar('CHARTS_START_TIME')

const UPDATE = 'UPDATE'
const UPDATE_CHART_DATA = 'UPDATE_CHART_DATA'
const UPDATE_TOP_PAIRS = 'UPDATE_TOP_PAIRS'
const UPDATE_HOURLY_DATA = 'UPDATE_HOURLY_DATA'

dayjs.extend(utc)

export function safeAccess(object, path) {
  return object
    ? path.reduce(
        (accumulator, currentValue) => (accumulator && accumulator[currentValue] ? accumulator[currentValue] : null),
        object
      )
    : null
}

const PairDataContext = createContext()

function usePairDataContext() {
  return useContext(PairDataContext)
}

function reducer(state, { type, payload }) {
  switch (type) {
    case UPDATE: {
      const { pairAddress, data } = payload
      return {
        ...state,
        [pairAddress]: {
          ...state?.[pairAddress],
          ...data,
        },
      }
    }

    case UPDATE_TOP_PAIRS: {
      const { topPairs } = payload
      let added = {}
      topPairs.map((pair) => {
        return (added[pair.id] = pair)
      })
      return {
        ...state,
        ...added,
      }
    }

    case UPDATE_CHART_DATA: {
      const { address, chartData } = payload
      return {
        ...state,
        [address]: {
          ...(safeAccess(state, [address]) || {}),
          chartData,
        },
      }
    }

    case UPDATE_HOURLY_DATA: {
      const { address, hourlyData, timeWindow } = payload
      return {
        ...state,
        [address]: {
          ...state?.[address],
          hourlyData: {
            ...state?.[address]?.hourlyData,
            [timeWindow]: hourlyData,
          },
        },
      }
    }

    default: {
      throw Error(`Unexpected action type in DataContext reducer: '${type}'.`)
    }
  }
}

export default function Provider({ children }) {
  const [state, dispatch] = useReducer(reducer, {})

  // update pair specific data
  const update = useCallback((pairAddress, data) => {
    dispatch({
      type: UPDATE,
      payload: {
        pairAddress,
        data,
      },
    })
  }, [])

  const updateTopPairs = useCallback((topPairs) => {
    dispatch({
      type: UPDATE_TOP_PAIRS,
      payload: {
        topPairs,
      },
    })
  }, [])

  const updateChartData = useCallback((address, chartData) => {
    dispatch({
      type: UPDATE_CHART_DATA,
      payload: { address, chartData },
    })
  }, [])

  const updateHourlyData = useCallback((address, hourlyData, timeWindow) => {
    dispatch({
      type: UPDATE_HOURLY_DATA,
      payload: { address, hourlyData, timeWindow },
    })
  }, [])

  return (
    <PairDataContext.Provider
      value={useMemo(
        () => [
          state,
          {
            update,
            updateChartData,
            updateTopPairs,
            updateHourlyData,
          },
        ],
        [state, update, updateChartData, updateTopPairs, updateHourlyData]
      )}
    >
      {children}
    </PairDataContext.Provider>
  )
}

async function getBulkPairData(pairList, nrgPrice) {
  const [t1, t2, tWeek] = getTimestampsForChanges()

  let result = await getBlocksFromTimestamps([t1, t2, tWeek])

  if (result.length === 3) {
    // HACK FOR WHEN GRAPH-NODE IS NOT FULLY SYNCED

    let [{ number: b1 }, { number: b2 }, { number: bWeek }] = result // await getBlocksFromTimestamps([t1, t2, tWeek])

    try {
      let current = await client.query({
        query: PAIRS_BULK,
        variables: {
          allPairs: pairList,
        },
        fetchPolicy: 'cache-first',
      })

      let [oneDayResult, twoDayResult, oneWeekResult] = await Promise.all(
        [b1, b2, bWeek].map(async (block) => {
          return client.query({
            query: PAIRS_HISTORICAL_BULK(block, pairList),
            fetchPolicy: 'cache-first',
          }).catch(() => ({ data: { pairs: [] } }))
        })
      )

      let oneDayStatistic = oneDayResult?.data?.pairs.reduce((obj, cur, i) => {
        return { ...obj, [cur.id]: cur }
      }, {})

      let twoDayStatistic = twoDayResult?.data?.pairs.reduce((obj, cur, i) => {
        return { ...obj, [cur.id]: cur }
      }, {})

      let oneWeekData = oneWeekResult?.data?.pairs.reduce((obj, cur, i) => {
        return { ...obj, [cur.id]: cur }
      }, {})

      // Fetch 48h of hourly pair stats — current 24h for volume, previous 24h for % change
      const { pairVolumeMap, prevPairVolumeMap } = await fetchHourlyPairVolumes().catch(() => ({ pairVolumeMap: {}, prevPairVolumeMap: {} }))

      // Fetch last 7 days of daily pair stats for weekly volume (APY calculation)
      const nowSec = dayjs.utc().unix()
      const sevenDaysAgo = String(Math.floor(nowSec / 86400) * 86400 - 7 * 86400)
      const dailyPairStatsResult = await client.query({
        query: DAILY_PAIR_STATS,
        variables: { dateFrom: sevenDaysAgo },
        fetchPolicy: 'cache-first',
      }).catch(() => ({ data: { dailyPairStatistics: [] } }))
      const weeklyPairVolumeMap = {}
      for (const stat of dailyPairStatsResult?.data?.dailyPairStatistics ?? []) {
        const pid = stat.pair.id
        weeklyPairVolumeMap[pid] = (weeklyPairVolumeMap[pid] ?? 0) + parseFloat(stat.dailyVolumeUSD ?? 0)
      }


      let pairData = await Promise.all(
        current &&
          current.data.pairs.map(async (pair) => {
            let data = pair
            let oneDayHistory = oneDayStatistic?.[pair.id]
            if (!oneDayHistory) {
              let newData = await client.query({ query: PAIR_DATA(pair.id, b1), fetchPolicy: 'cache-first' }).catch(() => ({ data: { pairs: [] } }))
              oneDayHistory = newData.data.pairs[0]
            }
            let twoDayHistory = twoDayStatistic?.[pair.id]
            if (!twoDayHistory) {
              let newData = await client.query({ query: PAIR_DATA(pair.id, b2), fetchPolicy: 'cache-first' }).catch(() => ({ data: { pairs: [] } }))
              twoDayHistory = newData.data.pairs[0]
            }
            let oneWeekHistory = oneWeekData?.[pair.id]
            if (!oneWeekHistory) {
              let newData = await client.query({ query: PAIR_DATA(pair.id, bWeek), fetchPolicy: 'cache-first' }).catch(() => ({ data: { pairs: [] } }))
              oneWeekHistory = newData.data.pairs[0]
            }
            data = parseData(data, oneDayHistory, twoDayHistory, oneWeekHistory, nrgPrice, b1)

            // When no block history, override with exact rolling 24h + matching % change
            if (!oneDayHistory) {
              const current24h = pairVolumeMap[pair.id] ?? 0
              const prev24h = prevPairVolumeMap[pair.id] ?? 0
              data.oneDayVolumeUSD = current24h
              data.oneWeekVolumeUSD = weeklyPairVolumeMap[pair.id] ?? 0
              data.volumeChangeUSD = getPercentChange(current24h, prev24h)
            }

            return data
          })
      )
      return pairData
    } catch (e) {
      console.log(e)
    }
  }
}

function parseData(data, oneDayStatistic, twoDayStatistic, oneWeekData, nrgPrice, oneDayBlock) {
  // get volume changes
  const [oneDayVolumeUSD, volumeChangeUSD] = get2DayPercentChange(
    data?.volumeUSD,
    oneDayStatistic?.volumeUSD ? oneDayStatistic.volumeUSD : 0,
    twoDayStatistic?.volumeUSD ? twoDayStatistic.volumeUSD : 0
  )

  const [oneDayVolumeUntracked, volumeChangeUntracked] = get2DayPercentChange(
    data?.untrackedVolumeUSD,
    oneDayStatistic?.untrackedVolumeUSD ? parseFloat(oneDayStatistic?.untrackedVolumeUSD) : 0,
    twoDayStatistic?.untrackedVolumeUSD ? twoDayStatistic?.untrackedVolumeUSD : 0
  )
  const oneWeekVolumeUSD = parseFloat(oneWeekData ? data?.volumeUSD - oneWeekData?.volumeUSD : data.volumeUSD)

  // set volume properties
  data.oneDayVolumeUSD = parseFloat(oneDayVolumeUSD)
  data.oneWeekVolumeUSD = oneWeekVolumeUSD
  data.volumeChangeUSD = volumeChangeUSD
  data.oneDayVolumeUntracked = oneDayVolumeUntracked
  data.volumeChangeUntracked = volumeChangeUntracked

  // set liquiditry properties
  data.trackedReserveUSD = data.trackedReserveNRG * nrgPrice
  data.liquidityChangeUSD = getPercentChange(data.reserveUSD, oneDayStatistic?.reserveUSD)

  // format if pair hasnt existed for a day or a week
  if (!oneDayStatistic && data && data.createdAtBlockNumber > oneDayBlock) {
    data.oneDayVolumeUSD = parseFloat(data.volumeUSD)
  }
  if (!oneDayStatistic && data) {
    data.oneDayVolumeUSD = parseFloat(data.volumeUSD)
  }
  if (!oneWeekData && data) {
    data.oneWeekVolumeUSD = parseFloat(data.volumeUSD)
  }
  if (data?.token0?.id === WNRG_ADDRESS) {
    data.token0.name = 'Energi (Wrapped)'
    data.token0.symbol = 'NRG'
  }
  if (data?.token1?.id === WNRG_ADDRESS) {
    data.token1.name = 'Energi (Wrapped)'
    data.token1.symbol = 'NRG'
  }
  return data
}

const getPairTransactions = (pairAddress) => async (windowStart, windowEnd, first, direction = 'desc') => {
  const transactions = {}

  try {
    let result = await client.query({
      query: FILTERED_TRANSACTIONS,
      variables: {
        allPairs: [pairAddress],
        first,
        windowStart: String(windowStart),
        windowEnd: String(windowEnd),
        orderDirection: direction,
      },
      fetchPolicy: 'no-cache',
    })
    transactions.mints = result.data.mints
    transactions.burns = result.data.burns
    transactions.swaps = result.data.swaps
  } catch (e) {
    console.log(e)
  }

  return transactions
}

const getPairChartData = async (pairAddress) => {
  let data = []
  const utcEndTime = dayjs.utc()
  let utcStartTime = utcEndTime.subtract(1, 'year').startOf('minute')
  let startTime = utcStartTime.unix() - 1

  try {
    let allFound = false
    let skip = 0
    while (!allFound) {
      let result = await client.query({
        query: PAIR_CHART,
        variables: {
          pairAddress: pairAddress,
          skip,
        },
        fetchPolicy: 'cache-first',
      })
      skip += 1000
      data = data.concat(result.data.dailyPairStatistics)
      if (result.data.dailyPairStatistics.length < 1000) {
        allFound = true
      }
    }

    let dayIndexSet = new Set()
    let dayIndexArray = []
    const oneDay = 24 * 60 * 60
    data.forEach((dailyStatistic, i) => {
      // add the day index to the set of days
      dayIndexSet.add((data[i].date / oneDay).toFixed(0))
      dayIndexArray.push(data[i])
      dailyStatistic.dailyVolumeUSD = parseFloat(dailyStatistic.dailyVolumeUSD)
      dailyStatistic.reserveUSD = parseFloat(dailyStatistic.reserveUSD)
    })

    if (data[0]) {
      // fill in empty days
      let timestamp = data[0].date ? data[0].date : startTime
      let latestLiquidityUSD = data[0].reserveUSD
      let index = 1
      while (timestamp < utcEndTime.unix() - oneDay) {
        const nextDay = timestamp + oneDay
        let currentDayIndex = (nextDay / oneDay).toFixed(0)
        if (!dayIndexSet.has(currentDayIndex)) {
          data.push({
            date: nextDay,
            dayString: nextDay,
            dailyVolumeUSD: 0,
            reserveUSD: latestLiquidityUSD,
          })
        } else {
          latestLiquidityUSD = dayIndexArray[index].reserveUSD
          index = index + 1
        }
        timestamp = nextDay
      }
    }

    data = data.sort((a, b) => (parseInt(a.date) > parseInt(b.date) ? 1 : -1))
  } catch (e) {
    console.log(e)
  }

  return data
}

const getHourlyRateData = async (pairAddress, startTime, latestBlock) => {
  try {
    const utcEndTime = dayjs.utc()
    let time = startTime

    // create an array of hour start times until we reach current hour
    const timestamps = []
    while (time <= utcEndTime.unix() - 3600) {
      timestamps.push(time)
      time += 3600
    }

    // backout if invalid timestamp format
    if (timestamps.length === 0) {
      return []
    }

    // once you have all the timestamps, get the blocks for each timestamp in a bulk query
    let blocks

    blocks = await getBlocksFromTimestamps(timestamps, 100)

    // catch failing case
    if (!blocks || blocks?.length === 0) {
      return []
    }

    if (latestBlock) {
      blocks = blocks.filter((b) => {
        return parseFloat(b.number) <= parseFloat(latestBlock)
      })
    }

    const result = await splitQuery(HOURLY_PAIR_RATES, client, [pairAddress], blocks, 100)

    // format token NRG price results
    let values = []
    for (var row in result) {
      let timestamp = row.split('t')[1]
      if (timestamp) {
        values.push({
          timestamp,
          rate0: parseFloat(result[row]?.token0Price),
          rate1: parseFloat(result[row]?.token1Price),
        })
      }
    }

    let formattedHistoryRate0 = []
    let formattedHistoryRate1 = []

    // for each hour, construct the open and close price
    for (let i = 0; i < values.length - 1; i++) {
      formattedHistoryRate0.push({
        timestamp: values[i].timestamp,
        open: parseFloat(values[i].rate0),
        close: parseFloat(values[i + 1].rate0),
      })
      formattedHistoryRate1.push({
        timestamp: values[i].timestamp,
        open: parseFloat(values[i].rate1),
        close: parseFloat(values[i + 1].rate1),
      })
    }

    return [formattedHistoryRate0, formattedHistoryRate1]
  } catch (e) {
    console.log(e)
    return [[], []]
  }
}

export function Updater() {
  const [, { updateTopPairs }] = usePairDataContext()
  const [nrgPrice] = useNrgPrice()
  useEffect(() => {
    async function getData() {
      // get top pairs by reserves
      let {
        data: { pairs },
      } = await client.query({
        query: PAIRS_CURRENT,
        fetchPolicy: 'cache-first',
      })

      // format as array of addresses
      const formattedPairs = pairs.map((pair) => {
        return pair.id
      })

      // get data for every pair in list
      let topPairs = await getBulkPairData(formattedPairs, nrgPrice)
      topPairs && updateTopPairs(topPairs)
    }
    nrgPrice && getData()
  }, [nrgPrice, updateTopPairs])
  return null
}

export function useHourlyRateData(pairAddress, timeWindow) {
  const [state, { updateHourlyData }] = usePairDataContext()
  const chartData = state?.[pairAddress]?.hourlyData?.[timeWindow]
  const [latestBlock] = useLatestBlocks()

  useEffect(() => {
    const currentTime = dayjs.utc()
    const windowSize = timeWindow === timeframeOptions.MONTH ? 'month' : 'week'
    const startTime =
      timeWindow === timeframeOptions.ALL_TIME
        ? parseInt(CHARTS_START_TIME, 10)
        : Math.max(currentTime.subtract(1, windowSize).startOf('hour').unix(), CHARTS_START_TIME)

    async function fetch() {
      let data = await getHourlyRateData(pairAddress, startTime, latestBlock)
      updateHourlyData(pairAddress, data, timeWindow)
    }
    if (!chartData) {
      fetch()
    }
  }, [chartData, timeWindow, pairAddress, updateHourlyData, latestBlock])

  return chartData
}

/**
 * @todo
 * store these updates to reduce future redundant calls
 */
export function useDataForList(pairList) {
  const [state] = usePairDataContext()
  const [nrgPrice] = useNrgPrice()

  const [stale, setStale] = useState(false)
  const [fetched, setFetched] = useState([])

  // reset
  useEffect(() => {
    if (pairList) {
      setStale(false)
      setFetched()
    }
  }, [pairList])

  useEffect(() => {
    async function fetchNewPairData() {
      let newFetched = []
      let unfetched = []

      pairList.map(async (pair) => {
        let currentData = state?.[pair.id]
        if (!currentData) {
          unfetched.push(pair.id)
        } else {
          newFetched.push(currentData)
        }
      })

      let newPairData = await getBulkPairData(
        unfetched.map((pair) => {
          return pair
        }),
        nrgPrice
      )
      setFetched(newFetched.concat(newPairData))
    }
    if (nrgPrice && pairList && pairList.length > 0 && !fetched && !stale) {
      setStale(true)
      fetchNewPairData()
    }
  }, [nrgPrice, state, pairList, stale, fetched])

  let formattedFetch =
    fetched &&
    fetched.reduce((obj, cur) => {
      return { ...obj, [cur?.id]: cur }
    }, {})

  return formattedFetch
}

/**
 * Get all the current and 24hr changes for a pair
 */
export function usePairData(pairAddress) {
  const [state, { update }] = usePairDataContext()
  const [nrgPrice] = useNrgPrice()
  const pairData = state?.[pairAddress]

  useEffect(() => {
    async function fetchData() {
      if (!pairData && pairAddress) {
        let data = await getBulkPairData([pairAddress], nrgPrice)
        data && update(pairAddress, data[0])
      }
    }
    if (!pairData && pairAddress && nrgPrice && isAddress(pairAddress)) {
      fetchData()
    }
  }, [pairAddress, pairData, update, nrgPrice])

  return pairData || {}
}

/**
 * Get most recent txns for a pair
 */
export function usePairTransactions(pairAddress) {
  return useTransactionFeed(getPairTransactions(pairAddress), pairAddress)
}

export function usePairChartData(pairAddress) {
  const [state, { updateChartData }] = usePairDataContext()
  const chartData = state?.[pairAddress]?.chartData

  useEffect(() => {
    async function checkForChartData() {
      if (!chartData) {
        let data = await getPairChartData(pairAddress)
        updateChartData(pairAddress, data)
      }
    }
    checkForChartData()
  }, [chartData, pairAddress, updateChartData])
  return chartData
}

/**
 * Get list of all pairs in Energiswap
 */
export function useAllPairData() {
  const [state] = usePairDataContext()
  return state || {}
}
